import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { UserDataService } from '../../shared/services/userData.service';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SummaryService } from '../../shared/services/eopSummary.service';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { Icons } from '../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { EopDownloadService } from '../../shared/services/eopDownload.service';
import { DateUtils } from '../../shared/helpers/dateUtils';
import { EopPlanSlug, getPlanSlugFromUrl } from '../eop-config/eop-page-config';

@Component({
  selector: 'summary',
  standalone: true,
  imports: [
    LucideAngularModule,
    CommonModule,
    PrvNextButtonsComponent,
    SpinnerComponent,
    PopupMessageComponent
  ],
  templateUrl: './summary.component.html',
  styleUrl: './summary.component.css'
})
export class SummaryComponent implements OnInit {
  currentRoute = "summary";

  icons = Icons;
  currentBuildingId: string | null = null;
  selectedSchoolYear: string = '';
  currentPlan: EopPlanSlug = 'al';

  loading: boolean = true;
  // Tracked separately from `loading` so a summary refresh can't pull the spinner
  // out from under an in-flight download.
  downloading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string = '';

  summary: { [stepName: string]: {
    section: string;
    last_updated_at: string,
    last_updated_user_name: string,
    date_approved: string,
    user_name_approved: string
  }[] } = {};


  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private userDataService: UserDataService,
    private summaryService: SummaryService,
    private eopDownloadService: EopDownloadService
  ) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const storedBuildingId = localStorage.getItem('current_building_id');
      if (storedBuildingId) {
        this.currentBuildingId = storedBuildingId;
        this.userDataService.setBuildingId(storedBuildingId);
      }
    }
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.selectedSchoolYear = DateUtils.getCurrentSchoolYear();
    this.currentPlan = getPlanSlugFromUrl(this.router.url);
    this.loadSummary();
    this.loadUserInfo();
  }

  loadUserInfo(): void {
    if (this.currentBuildingId) {
      this.userDataService.getUserInfo(this.currentBuildingId).subscribe({
        next: (response) => {
          if (!response.has_error) {
            const userData = response.data;
            this.userDataService.setUserData(userData); // Store user data in the service
            console.log('User info loaded successfully:', userData);
          } else {
            console.error('Error fetching user info:', response.message);
          }
        },
        error: (error) => {
          console.error('Error fetching user info:', error);
        }
      });
    }
  }

  loadSummary(): void {
    if (!this.currentBuildingId) return;

    this.loading = true;
    const summary$ = this.summaryService.getSummary(this.currentPlan, this.currentBuildingId);

    summary$.subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.summary = response.data;
          console.log('Summary loaded successfully:', this.summary);
        } else {
          console.error('Error fetching summary:', response.message);
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error fetching summary:', error);
      }
    });
  }

  // Keep the summary groups in the order the API returns them (which mirrors the
  // sidebar) instead of the keyvalue pipe's default alphabetical sort.
  preserveOrder = (): number => 0;

  getStatusIcon(last_updated_at: string | null | undefined): 'incomplete' | 'complete' | 'attention' {
    if (!last_updated_at) return 'incomplete';
  
    const updatedDate = new Date(last_updated_at);
    const now = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(now.getMonth() - 3); // ← changed from -1 to -3
  
    return updatedDate >= threeMonthsAgo ? 'complete' : 'attention';
  }

  downloadEop(): void {
    if (!this.currentBuildingId || this.downloading) return;

    // Generating the plan can take a while. Hold the spinner up for the whole
    // operation — request, base64 decode, and blob hand-off — and clear it in
    // finalize() so it also comes down on failure.
    this.downloading = true;

    // Full plan for the current EOP (no section) — same per-plan endpoint the
    // (now removed) Download/Print page used.
    this.eopDownloadService.downloadEopDocument(this.currentPlan, this.currentBuildingId, this.selectedSchoolYear)
      .pipe(finalize(() => this.downloading = false))
      .subscribe({
        next: (response) => {
          if (response.has_error || !response.data?.file_content) {
            console.error('No PDF content received');
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message || 'The EOP document could not be generated. Please try again.';
            return;
          }

          const { file_content, filename } = response.data;

          // Decode base64 into binary
          const byteCharacters = atob(file_content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);

          const blob = new Blob([byteArray], { type: 'application/pdf' });

          // Create and click download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename || `Emergency_Operations_Plan.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          console.error('Error downloading EOP document:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message || 'Error downloading the EOP document.';
        }
      });
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
  
  
  
}
